import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * ConfettiTrigger — poll `/reports/confetti-status` every 15 seconds while a
 * user is logged in and fire a canvas-confetti burst whenever the current
 * day's sales cross a new multiple of the admin-configured threshold. Uses
 * localStorage keyed by date so refreshing does not re-trigger, and the
 * counter naturally resets at midnight when the date changes.
 */
const STORAGE_KEY = "hakpi:confetti:lastSeen"; // "{date}:{multiple}"

function readSeen(date) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const [d, m] = raw.split(":");
    if (d !== date) return 0;
    const n = parseInt(m, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeSeen(date, multiple) {
  try { localStorage.setItem(STORAGE_KEY, `${date}:${multiple}`); } catch { /* yut */ }
}

function fireBurst(multiple, threshold) {
  const duration = 4000;
  const end = Date.now() + duration;
  const colors = ["#00A650", "#FFD400", "#FF3B30", "#0055FF", "#FF7A00", "#9333EA"];
  (function frame() {
    confetti({
      particleCount: 6,
      angle: 60,
      spread: 55,
      startVelocity: 60,
      origin: { x: 0, y: 0.9 },
      colors,
    });
    confetti({
      particleCount: 6,
      angle: 120,
      spread: 55,
      startVelocity: 60,
      origin: { x: 1, y: 0.9 },
      colors,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Merkezi büyük patlama
  confetti({
    particleCount: 200,
    spread: 100,
    origin: { y: 0.5 },
    colors,
    scalar: 1.2,
  });
  try {
    const formatted = new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(multiple * threshold);
    toast.success(`🎉 Günlük satış ${formatted} eşiğine ulaştı!`, {
      duration: 6000,
    });
  } catch { /* yut */ }
}

export default function ConfettiTrigger() {
  const { user } = useAuth();
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/reports/confetti-status");
        if (cancelled || !data) return;
        const threshold = Number(data.threshold) || 0;
        const highest = Number(data.highest_multiple_reached) || 0;
        const date = data.date || new Date().toISOString().slice(0, 10);
        if (threshold <= 0 || highest <= 0) return;
        const seen = readSeen(date);
        if (highest > seen) {
          // Her yeni katı için sırayla patlat (küçük gecikmelerle) — genelde 1 yeni kat olur.
          for (let m = seen + 1; m <= highest; m += 1) {
            const idx = m - seen - 1;
            setTimeout(() => fireBurst(m, threshold), idx * 800);
          }
          writeSeen(date, highest);
        }
      } catch { /* sessizce yut */ }
    };

    tick();
    intervalRef.current = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user]);

  return null;
}
